// Printed on physical labels: the values are permanent, the member names may be renamed.
export const StorageLabelKind = {
  WHEEL_SET: 'HKJ',
  STORAGE_UNIT: 'RF4',
  STORAGE_AREA: 'CFX'
} as const;

export type StorageLabelKind = (typeof StorageLabelKind)[keyof typeof StorageLabelKind];

export type StorageLabel = { kind: StorageLabelKind; identifier: string };

const PRODUCTION_SCHEME = 'com.noddi.noddiworkerapp://';
const PREVIEW_SCHEME = 'com.noddi.noddiworkerapp.preview://';
const LABEL_FORMAT_VERSION = 'v1';
// Wheel-set labels printed before the label format carried a kind.
const LEGACY_WHEEL_SET_SEGMENT = 'storageObject';

const STORAGE_LABEL_KINDS: readonly string[] = Object.values(StorageLabelKind);

const isStorageLabelKind = (value: string): value is StorageLabelKind => STORAGE_LABEL_KINDS.includes(value);

export const encodeStorageLabel = ({ kind, identifier, environment }: StorageLabel & { environment: string }) => {
  const scheme = environment === 'development' ? PREVIEW_SCHEME : PRODUCTION_SCHEME;
  return `${scheme}${LABEL_FORMAT_VERSION}/${kind}/${identifier}`;
};

/** Reads every label format ever printed; `null` for anything that is not a storage label. */
export const parseStorageLabel = (raw: string): StorageLabel | null => {
  const scheme = [PRODUCTION_SCHEME, PREVIEW_SCHEME].find((prefix) => raw.startsWith(prefix));
  if (!scheme) {
    return null;
  }
  const segments = raw.slice(scheme.length).split('/');

  if (segments.length === 2 && segments[0] === LEGACY_WHEEL_SET_SEGMENT && segments[1]) {
    return { kind: StorageLabelKind.WHEEL_SET, identifier: segments[1] };
  }
  if (segments.length === 3 && segments[0] === LABEL_FORMAT_VERSION && segments[2]) {
    const kind = segments[1];
    return kind && isStorageLabelKind(kind) ? { kind, identifier: segments[2] } : null;
  }
  return null;
};
